import { useMemo } from 'react'
import { useSelector, shallowEqual } from 'react-redux'
import { filterEcommerce } from '~/utils'
import { selectPosition } from '~/selectors'

export function useDataEcommerce(dataEcommerce, configEcommerce) {
  const [position] = useSelector(selectPosition, shallowEqual)

  const dataOffers = dataEcommerce?.offers ?? []

  const isEcommerceGoogle = configEcommerce?.buyOnline === 'ecommerceGoogle'
  const isViewLimited = Boolean(configEcommerce?.limitViews)
  const isViewOnlyStoresManuals = Boolean(configEcommerce?.checkStoreAddManually)
  const isViewOnlyStoresFeatured = Boolean(configEcommerce?.checkStoreFeatured)

  const isEnableAlertPrice = Boolean(configEcommerce?.showAlertPrice)

  const storesManuals = filterEcommerce(configEcommerce?.storesAddManually, position) || []
  const storesFeatured = configEcommerce?.storesFeatured || []
  const storesBlocked = configEcommerce?.blackList || []

  const hasShowPrice = Boolean(configEcommerce?.showProductValue)

  const hasStoresManuals = useMemo(() => {
    return isEcommerceGoogle && storesManuals.length > 0
  }, [isEcommerceGoogle, storesManuals])

  const hasStoresBlocked = useMemo(() => {
    return isEcommerceGoogle && storesManuals.length > 0
  }, [isEcommerceGoogle, storesBlocked])

  const hasStoresFeatured = useMemo(() => {
    return (
      isEcommerceGoogle &&
      dataOffers.filter(({ name }) => storesFeatured.some((store) => store?.name === name)).length >
        0
    )
  }, [isEcommerceGoogle, storesFeatured, dataOffers])

  const filteredOffers = dataOffers
    .filter(({ name }) =>
      isEcommerceGoogle ? !storesBlocked.some((store) => store?.name === name) : []
    )
    .filter(({ name }) =>
      isEcommerceGoogle ? !storesFeatured.some((store) => store?.name === name) : []
    )

  const dataOffersFeatured = storesFeatured
    .map(({ name }) => dataOffers.find((store) => store?.name === name))
    .filter((store) => Boolean(store))

  const totalOffers = useMemo(() => {
    if (isViewOnlyStoresFeatured && isViewOnlyStoresManuals)
      return storesManuals.length + dataOffersFeatured.length

    if (isViewOnlyStoresFeatured) return dataOffersFeatured.length
    if (isViewOnlyStoresManuals) return storesManuals.length

    return filteredOffers.length + storesManuals.length + dataOffersFeatured.length
  }, [
    isViewOnlyStoresFeatured,
    isViewOnlyStoresManuals,
    storesManuals,
    dataOffersFeatured,
    filteredOffers
  ])

  const offerMinMax = useMemo(() => {
    let min = 0,
      max = 0

    const priceOffers = [...filteredOffers, ...dataOffersFeatured].map(({ price }) => Number(price))

    min = Intl.NumberFormat('pt-BR', { style: 'decimal', minimumFractionDigits: 2 }).format(
      Math.min(...priceOffers)
    )
    max = Intl.NumberFormat('pt-BR', { style: 'decimal', minimumFractionDigits: 2 }).format(
      Math.max(...priceOffers)
    )

    return { min, max }
  }, [filteredOffers])

  const showProductValue = useMemo(() => {
    return isEcommerceGoogle && hasShowPrice
  }, [isEcommerceGoogle, hasShowPrice])

  const isEcommerceNotFound = useMemo(() => {
    if (isViewLimited) {
      if (isViewOnlyStoresManuals && hasStoresManuals) return false

      if (dataOffers.length && isViewOnlyStoresFeatured && hasStoresFeatured) return false
    }

    if (hasStoresManuals) return false

    if (filteredOffers.length) return false

    return true
  }, [
    dataOffers,
    hasStoresManuals,
    hasStoresFeatured,
    filteredOffers,
    isViewLimited,
    isViewOnlyStoresManuals,
    isViewOnlyStoresFeatured
  ])

  const showOffers = useMemo(() => {
    if (isViewOnlyStoresFeatured && isViewOnlyStoresManuals) {
      return hasStoresManuals && dataOffersFeatured.length > 1
    }

    if (isViewOnlyStoresManuals) return false
    if (isViewOnlyStoresFeatured) return dataOffersFeatured.length > 1

    return (hasStoresManuals && dataOffersFeatured.length > 1) || filteredOffers.length > 1
  }, [
    isViewOnlyStoresFeatured,
    isViewOnlyStoresManuals,
    dataOffersFeatured,
    hasStoresManuals,
    filteredOffers
  ])

  return {
    dataOffers,
    dataOffersFeatured,
    filteredOffers,
    hasStoresFeatured,
    hasStoresManuals,
    hasStoresBlocked,
    isEcommerceGoogle,
    isEcommerceNotFound,
    isEnableAlertPrice,
    isViewLimited,
    isViewOnlyStoresManuals,
    isViewOnlyStoresFeatured,
    offerMinMax,
    showOffers,
    showProductValue,
    storesFeatured,
    storesManuals,
    storesBlocked,
    totalOffers
  }
}
